import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ChatBubble,
  Close,
  DeleteOutline,
  AttachFileRounded,
  DescriptionOutlined,
  Inventory2Outlined,
  ApartmentRounded,
  ReceiptLongOutlined,
  SendRounded,
} from "@mui/icons-material";
import { API_ENDPOINT } from "../../services/api/resources";
import { useSelector } from "react-redux";
import styles from "./ChatBot.module.css";
import bidzHeaderLogo from "../../assets/images/svg/Logo.svg";
import bidzBotLogo from "../../assets/images/png/Logo@2x.png";
import { ACTION_PERMISSION_MAP } from "./permissionMap";
import { extractChatPrCode } from "./prNavigation";
import {
  clearPendingChatbotUpload,
  setPendingChatbotUpload,
} from "./chatTransferStore";
import NegotiationBot from "./NegotiationBot/NegotiationBot";
import { NEGOTIATION_BOT_OPEN_EVENT } from "./NegotiationBot/negotiationBotEvents";

const KEYWORD_SUGGESTIONS = {
  // RFQ RELATED

  rfq: { label: "Sourcing Events", action: "navigate_to_rfq_automation" },
  quotation: { label: "Sourcing Events", action: "navigate_to_rfq_automation" },
  quote: { label: "Sourcing Events", action: "navigate_to_rfq_automation" },
  "request for quote": {
    label: "Sourcing Events",
    action: "navigate_to_rfq_automation",
  },

  // QUICK RFQ
  "quick rfq": { label: "Xpress RFQ", action: "navigate_to_quick_rfq" },
  "fast rfq": { label: "Xpress RFQ", action: "navigate_to_quick_rfq" },
  "instant rfq": { label: "Xpress RFQ", action: "navigate_to_quick_rfq" },

  // PO RELATED

  po: { label: "PO Automation", action: "navigate_to_po_automation" },
  "purchase order": {
    label: "PO Automation",
    action: "navigate_to_po_automation",
  },
  order: { label: "PO Automation", action: "navigate_to_po_automation" },

  // PR RELATED

  pr: { label: "Purchase Requisitions", action: "navigate_to_pr_automation" },
  "purchase request": {
    label: "Purchase Requisitions",
    action: "navigate_to_pr_automation",
  },
  request: {
    label: "Purchase Requisitions",
    action: "navigate_to_pr_automation",
  },

  // INVOICE RELATED

  invoice: {
    label: "Trade Claims Automation",
    action: "navigate_to_merchant_invoice_automation",
  },
  bill: {
    label: "Trade Claims Automation",
    action: "navigate_to_merchant_invoice_automation",
  },
  payment: {
    label: "Trade Claims Automation",
    action: "navigate_to_merchant_invoice_automation",
  },
  billing: {
    label: "Trade Claims Automation",
    action: "navigate_to_merchant_invoice_automation",
  },

  // VENDOR RELATED

  vendor: { label: "Supplier Management", action: "navigate_to_vendor_master" },
  supplier: {
    label: "Supplier Management",
    action: "navigate_to_vendor_master",
  },
  party: { label: "Supplier Management", action: "navigate_to_vendor_master" },
  onboard: { label: "Supplier Management", action: "navigate_to_onboarding" },

  // BID RELATED

  bid: { label: "Bid Submission", action: "navigate_to_vendor_bid_submission" },
  tender: {
    label: "Bid Submission",
    action: "navigate_to_vendor_bid_submission",
  },
  "submit price": {
    label: "Bid Submission",
    action: "navigate_to_vendor_bid_submission",
  },

  // GRN RELATED

  grn: { label: "GRN Automation", action: "navigate_to_grn_automation" },
  "goods receipt": {
    label: "GRN Automation",
    action: "navigate_to_grn_automation",
  },
  "material received": {
    label: "GRN Automation",
    action: "navigate_to_grn_automation",
  },

  // MRP RELATED

  mrp: {
    label: "Material Requirement Planning",
    action: "navigate_to_material_requirement_planning",
  },
  planning: {
    label: "Material Requirement Planning",
    action: "navigate_to_material_requirement_planning",
  },
  "material plan": {
    label: "Material Requirement Planning",
    action: "navigate_to_material_requirement_planning",
  },

  // MATCHING RELATED

  matching: {
    label: "Three Way Matching",
    action: "navigate_to_three_way_matching",
  },
  "3 way": {
    label: "Three Way Matching",
    action: "navigate_to_three_way_matching",
  },
  "three way": {
    label: "Three Way Matching",
    action: "navigate_to_three_way_matching",
  },
  "match invoice": {
    label: "Three Way Matching",
    action: "navigate_to_three_way_matching",
  },

  // RATE CONTRACT

  rate: { label: "Rate Contract", action: "navigate_to_rate_contract" },
  contract: { label: "Rate Contract", action: "navigate_to_rate_contract" },

  // ITEM MASTER

  item: { label: "Item Master", action: "navigate_to_item_master" },
  product: { label: "Item Master", action: "navigate_to_item_master" },
  material: { label: "Item Master", action: "navigate_to_item_master" },

  // COMPARISON AUTOMATION

  compare: {
    label: "Comparison Automation",
    action: "navigate_to_comparison_automation",
  },
  comparison: {
    label: "Comparison Automation",
    action: "navigate_to_comparison_automation",
  },

  // COMPLIANCE

  compliance: {
    label: "Vendor Compliance Verification",
    action: "navigate_to_vendor_compliance_verification",
  },
  "verify vendor": {
    label: "Vendor Compliance Verification",
    action: "navigate_to_vendor_compliance_verification",
  },

  // DASHBOARD

  dashboard: { label: "Dashboard", action: "navigate_to_dashboard" },
  home: { label: "Dashboard", action: "navigate_to_dashboard" },

  // USER RELATED

  approval: {
    label: "Onboarding Approval",
    action: "navigate_to_user_onboarding_approval",
  },
  approve: {
    label: "Onboarding Approval",
    action: "navigate_to_user_onboarding_approval",
  },
};

const EMAIL_PR_TRIGGER_PATTERNS = [
  "create pr from email",
  "create pr from email content",
  "create pr",
  "create purchase request",
  "create purchase request from email",
  "extract pr from email",
  "extract purchase request from email",
  "email request",
  "email pr",
];

const extractPRCode = (message) => {
  return extractChatPrCode(message);
};

// const extractVendorSearchText = (message) => {
//   const words = message.toLowerCase().split(/\s+/);

//   const vendorIndex = words.findIndex(
//     (w) => w === "vendor" || w === "supplier"
//   );

//   if (vendorIndex === -1) return null;

//   const searchText = words
//     .slice(vendorIndex + 1)
//     .join(" ")
//     .trim();
//   return searchText || null;
// };

const extractRFQCode = (message) => {
  const regex = /\b([A-Z]+(?:-[A-Z]+)?[_-]?\d+)\b/i;
  const match = message.match(regex);
  return match ? match[0] : null;
};

const extractPOSearchValue = (message) => {
  const match = message.match(/\b(?:po|pr)\s+([a-zA-Z0-9_-]+)\b/i);
  return match ? match[1] : null;
};

const extractUserSearchText = (message) => {
  const match = message.match(/\buser[s]?\s+(.+)/i);
  return match ? match[1].trim() : null;
};

const extractVendorUSerSearch = (message) => {
  const match = message.match(/\buser[s]?\s+(.+)/i);
  return match ? match[1].trim() : null;
};

const extractVendorMasterSearch = (message) => {
  if (typeof message !== "string") return null;

  const stopWords = new Set([
    "open",
    "show",
    "get",
    "find",
    "search",
    "lookup",
    "view",
    "details",
    "profile",
    "information",
    "please",
    "can",
    "you",
    "for",
    "of",
    "the",
    "a",
    "an",
    "vendor", // ✅ IMPORTANT
    "vendors", // ✅ IMPORTANT
  ]);

  const words = message
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .filter(Boolean);

  const result = [];
  for (let i = words.length - 1; i >= 0; i--) {
    if (stopWords.has(words[i])) break;
    result.unshift(words[i]);
  }

  return result.length ? result.join(" ") : null;
};

const extractInvoiceOrPONumber = (message) => {
  if (typeof message !== "string") return null;

  // Match any structured identifier (letters + numbers)
  const regex =
    /\b[A-Z]{1,10}\d+|\b\d+[A-Z]{1,10}|\b[A-Z0-9]+(?:[-_/][A-Z0-9]+)+\b/i;

  const match = message.match(regex);
  return match ? match[0].trim() : null;
};

const extractMasterSearch = (message) => {
  if (typeof message !== "string") return null;

  const MASTER_KEYS = ["item", "tax", "cost center", "gl code", "user"];

  const text = message
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .trim();

  for (const key of MASTER_KEYS) {
    const regexKey = key.replace(/\s+/g, "\\s+");
    const regex = new RegExp(`\\b${regexKey}\\b(?:\\s+(.+))?`);
    const match = text.match(regex);

    if (match) {
      return {
        masterKey: key,
        searchText: match[1] ? match[1].trim() : null,
      };
    }
  }

  return null;
};

const extractThreeWaySearchText = (message) => {
  if (typeof message !== "string") return null;

  let cleaned = message.toLowerCase();

  // Remove common phrases first
  cleaned = cleaned.replace(
    /\b(three way matching|three way match|matching record|twm record|twm approval record|invoice matching record|matching details|approval record)\b/g,
    "",
  );

  // Remove intent words
  cleaned = cleaned.replace(
    /\b(open|view|search|find|lookup|filter|show|get|records?|record|details?|matching|match|approval|by|for|of|the|a|an)\b/g,
    "",
  );

  // Keep only allowed characters
  cleaned = cleaned.replace(/[^\w\s/.-]/g, "").trim();

  if (!cleaned) return null;

  // Extract last meaningful token
  const words = cleaned.split(/\s+/);
  return words.slice(-3).join(" ").trim() || null;
};

const extractApproverRangeMasterSearch = (message) => {
  if (typeof message !== "string") return null;

  const text = message
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .trim();

  // Must be approver-related
  if (!/(approver|approval)/.test(text)) return null;

  const APPROVER_MASTER_KEYS = [
    { key: "pr", aliases: ["pr", "purchase request"] },
    { key: "po", aliases: ["po", "purchase order"] },
    {
      key: "twm",
      aliases: ["twm", "three way matching", "invoice matching", "3 way"],
    },
  ];

  for (const master of APPROVER_MASTER_KEYS) {
    if (master.aliases.some((a) => text.includes(a))) {
      // Capture everything after "approver range"
      const match = text.match(/approver\s+range\s*(.+)?$/);

      return {
        masterKey: master.key,
        searchText: match?.[1]?.trim() || null,
      };
    }
  }

  return null;
};

const extractGrnSearchText = (message) => {
  if (typeof message !== "string") return null;

  const cleaned = message
    .toLowerCase()
    .replace(/[^\w\s/.-]/g, "") // keep numbers & refs
    .replace(
      /\b(search|find|lookup|view|show|filter|grn|grns|goods|receipt|note|by|for|of|the|a|an|open|pending|number)\b/g,
      "",
    )
    .trim();

  if (!cleaned) return null;

  // take last meaningful chunk (safe for names & numbers)
  const words = cleaned.split(/\s+/);
  const searchText = words.slice(-6).join(" ").trim();

  return searchText || null;
};

const extractComparisonSearch = (message) => {
  if (typeof message !== "string") return null;
  const matches = message.match(/\d+/g);
  return matches ? matches[matches.length - 1] : null;
};

const extractRCNumber = (message) => {
  if (typeof message !== "string") return null;

  const match = message.match(/rc\/\d{4}\/\d+/i);
  return match ? match[0].toUpperCase() : null;
};

export const extractRcToPoData = (message) => {
  const text = message.toLowerCase().trim();

  const rcToPoPatterns = [
    "convert rate contract to po",
    "create po from rate contract",
    "convert rc to po",
    "create purchase order from rate contract",
    "rate contract to po",
    "generate po from rc",
    "create po against rate contract",
    "convert rate contract into purchase order",
    "make po from rate contract",
    "create purchase order against rc",
  ];

  const actionMatched = rcToPoPatterns.some((p) => text.includes(p));
  if (!actionMatched) return null;

  const rcMatch = message.match(/rc\/\d{4}\/\d+/i);
  const rcNumber = rcMatch ? rcMatch[0].toUpperCase() : null;

  let remainingText = text;

  if (rcMatch) {
    remainingText = remainingText.replace(rcMatch[0].toLowerCase(), "");
  }

  rcToPoPatterns.forEach((p) => {
    remainingText = remainingText.replace(p, "");
  });

  // ✅ Split ONLY by comma, keep phrases intact
  let items = remainingText
    .split(",")
    .map((i) => i.trim())
    .filter((i) => i.length > 0);

  items = [...new Set(items)];

  return {
    action: "convert_rc_to_po",
    rc_number: rcNumber,
    item_values: items,
  };
};

const getDefaultMessages = () => [
  {
    sender: "bot",
    text: "Hi! I'm your bidZ.ai Assistant. How can I help you today?",
  },
];

const looksLikeEmailContent = (message) => {
  const text = String(message || "").trim();
  if (!text || text.length < 40) return false;

  return (
    /(^from:|^subject:|^dear\b|^hi\b|regards\b|thanks\b|please approve\b|please process\b)/im.test(
      text,
    ) ||
    (text.includes("\n") && /@/.test(text)) ||
    /^hi\s+[a-z]/i.test(text)
  );
};

const looksLikePrTableContent = (message) => {
  const text = String(message || "").trim();
  if (!text) return false;

  return (
    isMarkdownTable(text) ||
    isTabSeparatedTable(text) ||
    looksLikePrKeyValueItemContent(text) ||
    looksLikeWrappedPrLineItems(text)
  );
};

const looksLikeWrappedPrLineItems = (text = "") => {
  const normalized = String(text || "").replace(/\s+/g, " ").trim();
  if (!normalized) return false;

  const hasLineItemsHeader = /\bline\s+items?\b/i.test(normalized);
  const hasPrColumns =
    /\bsl\b/i.test(normalized) &&
    /\bpart\s+no\b/i.test(normalized) &&
    /\bitem\s+name\b/i.test(normalized) &&
    /\bqty\b/i.test(normalized) &&
    /\btax\b/i.test(normalized) &&
    /\brate\b/i.test(normalized);
  const hasNumberedRows = /\b\d{1,3}\s+[A-Z0-9-]{3,}\s+.+?\s+\d+(?:\.\d+)?\s+\d+(?:\.\d+)?%\s+(?:\u20b9|INR|Rs\.?)\s*[\d,]+(?:\.\d{2})?/i.test(
    normalized,
  );

  return (hasLineItemsHeader && hasPrColumns) || (hasPrColumns && hasNumberedRows);
};

// const getFinancialYearLabel = (date = new Date()) => {
//   const year = date.getFullYear();
//   const month = date.getMonth();
//   if (month >= 3) {
//     return `${year}-${String(year + 1).slice(-2)}`;
//   }
//   return `${year - 1}-${String(year).slice(-2)}`;
// };

// const getMonthCode = (date = new Date()) =>
//   date
//     .toLocaleString("en-US", { month: "short" })
//     .replace(".", "")
//     .toUpperCase();

const mapExtractedItemsForPrSave = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => {
      const unitPrice = parseFloat(item?.unit_price ?? item?.unitPrice ?? 0);
      const totalPrice = parseFloat(item?.total_price ?? item?.totalPrice ?? 0);

      return {
        itemName: item?.item_name || item?.itemName || "",
        itemCode: item?.item_code || item?.itemCode || "",
        quantity: String(item?.quantity ?? "1"),
        unit: item?.unit_measure || item?.unit || "Nos",
        description: item?.description || item?.item_name || "",
        category: item?.category || "",
        expenseType: item?.expense_type || item?.expenseType || "",
        expense_type: item?.expense_type || item?.expenseType || "",
        partnumber: item?.part_number || item?.partnumber || "",
        hsnCode: item?.hsn_code || item?.hsnCode || "",
        unspscCode: item?.unspsc_code || item?.unspscCode || "",
        unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
        totalPrice: Number.isFinite(totalPrice) ? totalPrice : 0,
      };
    })
    .filter((item) => item.itemName || item.itemCode);

const cloneFormData = (source) => {
  const cloned = new FormData();
  if (!source || typeof source.forEach !== "function") {
    return cloned;
  }

  source.forEach((value, key) => {
    cloned.append(key, value);
  });

  return cloned;
};
const isMarkdownTable = (text = "") => {
  const lines = String(text)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    lines.some((line) => line.startsWith("|") && line.endsWith("|")) &&
    lines.some((line) =>
      /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line),
    )
  );
};

const isTabSeparatedTable = (text = "") => {
  const lines = String(text).trim().split(/\r?\n/).filter(Boolean);

  return lines.length >= 2 && lines.every((line) => line.includes("\t"));
};

const convertTsvToMarkdown = (text = "") => {
  const rows = String(text)
    .trim()
    .split(/\r?\n/)
    .filter(Boolean)
    .map((line) => line.split("\t").map((cell) => cell.trim()));

  if (rows.length < 2) return text;

  const columnCount = Math.max(...rows.map((row) => row.length));

  const normalizedRows = rows.map((row) => {
    const nextRow = [...row];
    while (nextRow.length < columnCount) nextRow.push("");
    return nextRow;
  });

  const header = normalizedRows[0];
  const body = normalizedRows.slice(1);
  const separator = Array(columnCount).fill("---");

  return [header, separator, ...body]
    .map((row) => `| ${row.join(" | ")} |`)
    .join("\n");
};

const escapeMarkdownTableCell = (value = "") =>
  String(value).replace(/\|/g, "\\|").trim();

const parseModelCapacityRow = (line = "") => {
  const text = String(line || "")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return null;

  const capacityMatch = text.match(
    /(?:[-–—]\s*)?(\d+(?:\.\d+)?\s*(?:-?\s*)?(?:inch|inches|in|cm|mm|kg|g|gb|tb|mah|liters?|litres?|tons?)\b)$/i,
  );

  if (!capacityMatch) return null;

  const capacity = capacityMatch[1].replace(/\s*-\s*/g, "-").trim();
  const withoutCapacity = text
    .slice(0, capacityMatch.index)
    .replace(/[-–—]\s*$/, "")
    .trim();
  const firstSpaceIndex = withoutCapacity.search(/\s/);

  if (firstSpaceIndex <= 0) return null;

  const make = withoutCapacity.slice(0, firstSpaceIndex).trim();
  const model = withoutCapacity.slice(firstSpaceIndex + 1).trim();

  if (!make || !model) return null;

  return [make, model, capacity];
};

const convertModelCapacityListToMarkdown = (text = "") => {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) return "";

  const hasModelCapacityHeader = lines.some((line) =>
    /\bmake\b.*\bmodel\b.*\bcapacity\b/i.test(line),
  );
  if (!hasModelCapacityHeader) return "";

  const rows = lines.map(parseModelCapacityRow).filter(Boolean);
  if (rows.length < 1) return "";

  return [
    "| Make | TV Model | Capacity |",
    "| --- | --- | --- |",
    ...rows.map(
      (row) => `| ${row.map(escapeMarkdownTableCell).join(" | ")} |`,
    ),
  ].join("\n");
};

const ITEM_DETAIL_COLUMN_ORDER = [
  "item",
  "item name",
  "item code",
  "description",
  "quantity",
  "tax",
  "unit price",
  "total price",
  "hsn code",
  "unspsc code",
];

const ITEM_DETAIL_LABELS = {
  item: "Item",
  "item name": "Item Name",
  "item code": "Item Code",
  description: "Description",
  quantity: "Quantity",
  tax: "Tax",
  "unit price": "Unit Price",
  "total price": "Total Price",
  "hsn code": "HSN Code",
  "unspsc code": "UNSPSC Code",
};

const ITEM_DETAIL_KEY_ALIASES = {
  item: "item",
  "item no": "item",
  "item number": "item",
  "item name": "item name",
  product: "item name",
  material: "item name",
  "part no": "item code",
  "part number": "item code",
  "part no.": "item code",
  "part num": "item code",
  "item code": "item code",
  code: "item code",
  description: "description",
  desc: "description",
  qty: "quantity",
  quantity: "quantity",
  tax: "tax",
  gst: "tax",
  rate: "unit price",
  "unit rate": "unit price",
  "unit price": "unit price",
  price: "unit price",
  amount: "total price",
  total: "total price",
  "total amount": "total price",
  "total price": "total price",
  hsn: "hsn code",
  "hsn code": "hsn code",
  unspsc: "unspsc code",
  "unspsc code": "unspsc code",
};

const normalizeDetailKey = (key = "") =>
  ITEM_DETAIL_KEY_ALIASES[String(key || "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ")] || "";

const isItemDetailStartLine = (line = "") =>
  /^item\s*(?:no|number|#|:|-)?\s*\d+\b/i.test(String(line || "").trim());

const extractItemDetailStartNumber = (line = "") => {
  const match = String(line || "")
    .trim()
    .match(/^item\s*(?:no|number|#|:|-)?\s*(\d+)\b/i);
  return match ? match[1] : "";
};

const looksLikePrKeyValueItemContent = (text = "") => {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 3 || !lines.some(isItemDetailStartLine)) return false;

  const fields = new Set();
  lines.forEach((line) => {
    const fieldMatch = line.match(/^([^:]{2,40}):\s*(.*)$/);
    if (!fieldMatch) return;

    const key = normalizeDetailKey(fieldMatch[1]);
    if (key) fields.add(key);
  });

  const hasIdentity = fields.has("item name") || fields.has("item code");
  const hasCommercials =
    fields.has("quantity") &&
    (fields.has("unit price") || fields.has("total price"));

  return hasIdentity && hasCommercials;
};

const normalizeAmountDisplayValue = (value = "") => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^(?:inr|rs\.?|\u20b9)/i.test(text)) return text;
  if (!/^\d[\d,\s]*(?:\.\d+)?$/.test(text)) return text;
  return `INR ${text}`;
};

const convertItemDetailBlocksToMarkdown = (text = "") => {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 3) return "";
  if (!lines.some(isItemDetailStartLine)) return "";

  const items = [];
  let currentItem = null;
  let currentField = "";

  lines.forEach((line) => {
    const itemNumber = extractItemDetailStartNumber(line);
    if (itemNumber) {
      currentItem = { item: `Item ${itemNumber}` };
      currentField = "";
      items.push(currentItem);
      return;
    }

    if (!currentItem) return;

    const fieldMatch = line.match(/^([^:]{2,40}):\s*(.*)$/);
    if (fieldMatch) {
      const key = normalizeDetailKey(fieldMatch[1]);
      if (!ITEM_DETAIL_LABELS[key]) {
        currentField = "";
        return;
      }

      currentField = key;
      currentItem[currentField] =
        key === "unit price" || key === "total price"
          ? normalizeAmountDisplayValue(fieldMatch[2])
          : fieldMatch[2].trim();
      return;
    }

    if (currentField) {
      currentItem[currentField] = `${currentItem[currentField]} ${line}`.trim();
    }
  });

  const rows = items.filter(
    (item) =>
      Object.keys(item).filter((key) => key !== "item" && item[key]).length >= 2,
  );

  if (!rows.length) return "";

  const columns = ITEM_DETAIL_COLUMN_ORDER.filter((key) =>
    rows.some((row) => row[key]),
  );

  return [
    `| ${columns.map((key) => ITEM_DETAIL_LABELS[key]).join(" | ")} |`,
    `| ${columns.map(() => "---").join(" | ")} |`,
    ...rows.map(
      (row) =>
        `| ${columns
          .map((key) => escapeMarkdownTableCell(row[key] || "-"))
          .join(" | ")} |`,
    ),
  ].join("\n");
};

const extractMatchValue = (text = "", regex) => {
  const match = String(text || "").match(regex);
  return match ? String(match[1] || "").trim() : "";
};

const cleanItemName = (value = "") =>
  String(value || "")
    .replace(/^po\s*(?:no|number)\s*[:#-]?\s*[A-Z0-9-]+\s*,?\s*/i, "")
    .replace(/^another\s+item\s+/i, "")
    .replace(/^item\s+/i, "")
    .replace(/\s+/g, " ")
    .trim();

const cleanAmountValue = (value = "") =>
  String(value || "")
    .replace(/[,\s]+$/g, "")
    .trim();

const getUnavailableCodeValue = (segment = "", codeName = "") => {
  const regex = new RegExp(
    `(?:${codeName}\\s+(?:code\\s+)?(?:also\\s+)?(?:is\\s+)?(?:not\\s+available|not\\s+specified|not\\s+mentioned|nil|not\\s+given)|hsn\\s+and\\s+unspsc\\s+(?:are\\s+)?(?:not\\s+specified|not\\s+mentioned|not\\s+available))`,
    "i",
  );

  if (regex.test(segment)) return "Not specified";
  return extractMatchValue(
    segment,
    new RegExp(`${codeName}\\s+code\\s*(?:is|:)?\\s*([A-Z0-9-]+)`, "i"),
  );
};

const parseProseItemSegment = (segment = "", index = 0) => {
  const text = String(segment || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return null;

  const codeMatch = text.match(/\b(?:item\s+code|code)\s+([A-Z0-9-]+)/i);
  if (!codeMatch) return null;

  const name = cleanItemName(text.slice(0, codeMatch.index));
  const itemCode = codeMatch[1];
  const quantity = extractMatchValue(
    text,
    /\b(?:qty|quantity)\s*(?:is)?\s*[:#-]?\s*(\d+(?:\.\d+)?)/i,
  );
  const unitPrice = extractMatchValue(
    text,
    /\b(?:each\s+unit\s+price|unit\s+rate|rate|unit\s+price)\s*(?:rs|inr)?\s*(?:is|:)?\s*([0-9,]+(?:\.\d+)?)/i,
  );
  const totalPrice = cleanAmountValue(
    extractMatchValue(
      text,
      /\b(?:total\s+price\s+comes|total\s+amount|total\s+price|total)\s*(?:rs|inr)?\s*(?:comes|is|:)?\s*([0-9,]+(?:\.\d+)?)/i,
    ),
  );
  const description = extractMatchValue(
    text,
    /\bdescription\s+(.+?)(?=\s+(?:hsn\s+code|unspsc\s+code|subtotal|tax|final\s+grand\s+total)\b|$)/i,
  );

  return {
    item: `Item ${index + 1}`,
    "item name": name,
    "item code": itemCode,
    description,
    quantity,
    "unit price": unitPrice ? `INR ${cleanAmountValue(unitPrice)}` : "",
    "total price": totalPrice ? `INR ${totalPrice}` : "",
    "hsn code": getUnavailableCodeValue(text, "hsn"),
    "unspsc code": getUnavailableCodeValue(text, "unspsc"),
  };
};

const buildItemDetailsMarkdownTable = (rows = []) => {
  const validRows = rows.filter(
    (row) =>
      row &&
      Object.keys(row).filter((key) => key !== "item" && row[key]).length >= 2,
  );

  if (!validRows.length) return "";

  const columns = ITEM_DETAIL_COLUMN_ORDER.filter((key) =>
    validRows.some((row) => row[key]),
  );

  return [
    `| ${columns.map((key) => ITEM_DETAIL_LABELS[key]).join(" | ")} |`,
    `| ${columns.map(() => "---").join(" | ")} |`,
    ...validRows.map(
      (row) =>
        `| ${columns
          .map((key) => escapeMarkdownTableCell(row[key] || "-"))
          .join(" | ")} |`,
    ),
  ].join("\n");
};

const convertPoItemProseToMarkdown = (text = "") => {
  const normalizedText = String(text || "")
    .replace(/\s+/g, " ")
    .trim();

  if (!/\b(?:po\s*(?:no|number)?|item\s+code|subtotal|grand\s+total)\b/i.test(normalizedText)) {
    return "";
  }

  const subtotalIndex = normalizedText.search(/\bsubtotal\b/i);
  const itemText =
    subtotalIndex >= 0 ? normalizedText.slice(0, subtotalIndex) : normalizedText;

  const itemSegments = itemText
    .split(
      /\.\s*(?=another\s+item\b|[A-Z0-9][A-Za-z0-9-]*(?:\s+[A-Za-z0-9-]+){0,6}\s+item\s+code\b)/i,
    )
    .map((segment) => segment.trim())
    .filter((segment) => /\b(?:item\s+code|code)\s+[A-Z0-9-]+/i.test(segment));

  const rows = itemSegments
    .map(parseProseItemSegment)
    .filter(Boolean);
  const itemTable = buildItemDetailsMarkdownTable(rows);

  if (!itemTable) return "";

  const poNumber = extractMatchValue(
    normalizedText,
    /\bpo\s*(?:no|number)?\s*[:#-]?\s*([A-Z0-9-]+)/i,
  );
  const subtotal = extractMatchValue(
    normalizedText,
    /\bsubtotal\s*(?:rs|inr)?\s*(?:is|:)?\s*([0-9,]+(?:\.\d+)?)/i,
  );
  const taxPercent = extractMatchValue(
    normalizedText,
    /\btax\s+(\d+(?:\.\d+)?)\s*(?:percentage|percent|%)\b/i,
  );
  const taxAmount = extractMatchValue(
    normalizedText,
    /\btax\s+amount\s*(?:rs|inr)?\s*(?:is|:)?\s*([0-9,]+(?:\.\d+)?)/i,
  );
  const grandTotal = extractMatchValue(
    normalizedText,
    /\b(?:final\s+grand\s+total|grand\s+total)\s*(?:rs|inr)?\s*(?:is|:)?\s*([0-9,]+(?:\.\d+)?)/i,
  );

  const summaryRows = [
    poNumber ? ["PO No", poNumber] : null,
    subtotal ? ["Subtotal", `INR ${subtotal}`] : null,
    taxPercent ? ["Tax", `${taxPercent}%`] : null,
    taxAmount ? ["Tax Amount", `INR ${taxAmount}`] : null,
    grandTotal ? ["Grand Total", `INR ${grandTotal}`] : null,
  ].filter(Boolean);

  if (!summaryRows.length) return itemTable;

  const summaryTable = [
    "| Field | Value |",
    "| --- | --- |",
    ...summaryRows.map(
      ([label, value]) =>
        `| ${escapeMarkdownTableCell(label)} | ${escapeMarkdownTableCell(
          value,
        )} |`,
    ),
  ].join("\n");

  return `${itemTable}\n\n${summaryTable}`;
};

const convertHtmlTableToMarkdown = (html = "") => {
  if (!html || typeof window === "undefined") return "";

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const table = doc.querySelector("table");
  if (!table) return "";

  const rows = Array.from(table.querySelectorAll("tr"))
    .map((row) =>
      Array.from(row.querySelectorAll("th, td")).map((cell) =>
        cell.textContent.replace(/\s+/g, " ").trim(),
      ),
    )
    .filter((row) => row.some((cell) => cell));

  if (rows.length < 2) return "";

  const columnCount = Math.max(...rows.map((row) => row.length));
  const normalizedRows = rows.map((row) => {
    const nextRow = [...row];
    while (nextRow.length < columnCount) nextRow.push("");
    return nextRow;
  });

  const header = normalizedRows[0];
  const body = normalizedRows.slice(1);
  const separator = Array(columnCount).fill("---");

  return [header, separator, ...body]
    .map((row) => `| ${row.join(" | ")} |`)
    .join("\n");
};

const normalizePastedTable = (text = "") => {
  if (isTabSeparatedTable(text)) {
    return convertTsvToMarkdown(text);
  }

  const modelCapacityMarkdown = convertModelCapacityListToMarkdown(text);
  if (modelCapacityMarkdown) return modelCapacityMarkdown;

  const itemDetailsMarkdown = convertItemDetailBlocksToMarkdown(text);
  if (itemDetailsMarkdown) return itemDetailsMarkdown;

  const poItemProseMarkdown = convertPoItemProseToMarkdown(text);
  if (poItemProseMarkdown) return poItemProseMarkdown;

  return text;
};

const normalizeClipboardPayload = (clipboardData) => {
  if (!clipboardData) return "";

  const html = clipboardData.getData("text/html");
  const htmlTableMarkdown = convertHtmlTableToMarkdown(html);
  if (htmlTableMarkdown) return htmlTableMarkdown;

  const text = clipboardData.getData("text/plain");
  return normalizePastedTable(text);
};

const MessageText = ({ text }) => {
  return (
    <div className={styles.messageMarkdown}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          table: ({ children }) => (
            <div className={styles.markdownTableWrap}>
              <table className={styles.chatMarkdownTable}>{children}</table>
            </div>
          ),
          p: ({ children }) => (
            <p className={styles.chatMarkdownParagraph}>{children}</p>
          ),
        }}
      >
        {String(normalizeChatMessageText(text) || "")}
      </ReactMarkdown>
    </div>
  );
};

const normalizeChatMessageText = (text = "") => normalizePastedTable(text);

const isChatTableText = (text = "") =>
  isMarkdownTable(normalizeChatMessageText(text));

const formatCurrency = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) return "-";

  return amount.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const buildEmailPrReasonFromItems = (items = []) => {
  const uniqueDescriptions = [];
  const seen = new Set();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const description = String(
      item?.description || item?.item_name || item?.itemName || "",
    ).trim();

    if (!description) return;

    const normalizedKey = description.toLowerCase();
    if (seen.has(normalizedKey)) return;

    seen.add(normalizedKey);
    uniqueDescriptions.push(description);
  });

  return uniqueDescriptions.join("\n");
};

const parseEmailPrAmount = (value = "") => {
  const amount = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const splitWrappedPrItemText = (value = "") => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return { itemName: "", description: "" };

  const productTypeMatch = text.match(
    /\b(desktop|monitor|laptop|notebook|server|printer|keyboard|mouse|tablet|phone|mobile)\b/i,
  );

  if (productTypeMatch) {
    const endIndex = productTypeMatch.index + productTypeMatch[0].length;
    return {
      itemName: text.slice(0, endIndex).trim(),
      description: text.slice(endIndex).trim(),
    };
  }

  const words = text.split(/\s+/);
  return {
    itemName: words.slice(0, 4).join(" ").trim(),
    description: words.slice(4).join(" ").trim(),
  };
};

const extractWrappedPrLineItems = (message = "") => {
  if (!looksLikeWrappedPrLineItems(message)) return [];

  const sourceLines = String(message || "")
    .replace(/\u00a0/g, " ")
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  const segments = [];
  let currentSegment = "";

  sourceLines.forEach((line) => {
    if (/^\d{1,3}\s+[A-Z0-9-]+\b/i.test(line)) {
      if (currentSegment) segments.push(currentSegment);
      currentSegment = line;
      return;
    }

    if (currentSegment) {
      currentSegment = `${currentSegment} ${line}`.trim();
    }
  });

  if (currentSegment) segments.push(currentSegment);

  return segments
    .map((segment) => {
      const match = segment.match(
        /^\s*(\d{1,3})\s+([A-Z0-9-]+)\s+(.+?)\s+([A-Z0-9-]+|[-\u2013\u2014])\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(?:\u20b9|INR|Rs\.?)?\s*([\d,]+(?:\.\d{1,2})?)\s+(?:\u20b9|INR|Rs\.?)?\s*([\d,]+(?:\.\d{1,2})?)\s*$/i,
      );

      if (!match) return null;

      const [, serialNo, partNumber, itemText, hsnCode, quantity, taxRate, unitPrice, lineAmount] =
        match;
      const { itemName, description } = splitWrappedPrItemText(itemText);

      return {
        serial_no: serialNo,
        item_name: itemName || itemText,
        item_code: partNumber,
        part_number: partNumber,
        description: description || itemText,
        quantity,
        unit_measure: "Nos",
        hsn_code: /^[-\u2013\u2014]$/.test(hsnCode) ? "" : hsnCode,
        tax_rate: taxRate,
        unit_price: parseEmailPrAmount(unitPrice),
        total_price: parseEmailPrAmount(lineAmount),
      };
    })
    .filter(Boolean);
};

const buildLocalEmailPrExtractionResponse = (message = "") => {
  const items = extractWrappedPrLineItems(message);
  if (!items.length) return null;

  const totalPrice = items.reduce(
    (sum, item) => sum + parseEmailPrAmount(item.total_price),
    0,
  );
  const purchaseReason = buildEmailPrReasonFromItems(items);

  return {
    success: true,
    data: {
      items,
      request_reason: purchaseReason,
      notes: "Extracted from pasted line items.",
      total_price: totalPrice,
    },
    save_payload: {
      items,
      purchase_reason: purchaseReason,
      total_price: totalPrice,
    },
  };
};

const ChatWidget = () => {
  const clearChatURLParams = () => {
    const hash = window.location.hash;
    const baseRoute = hash.split("?")[0];
    window.history.replaceState(null, "", baseRoute);
  };

  const getNegotiationContextFromUrl = () => {
    const hashQuery = window.location.hash.includes("?")
      ? window.location.hash.split("?")[1]
      : "";
    const searchParams = new URLSearchParams(window.location.search || "");
    const hashParams = new URLSearchParams(hashQuery || "");
    const getParam = (key) => hashParams.get(key) || searchParams.get(key) || "";
    const negotiationSessionToken =
      getParam("negotiation_session_token") || getParam("session_token");
    const vendorBidItemToken =
      getParam("vendor_bid_item_token") || getParam("bid_item_token");

    if (!negotiationSessionToken && !vendorBidItemToken) return null;

    return {
      mode: "vendor",
      negotiationSessionToken,
      vendorBidItemToken,
      rfqCode: getParam("rfq_code") || getParam("rfqCode"),
      rfqToken: getParam("rfq_token") || getParam("rfqToken"),
      itemName: getParam("item_name") || getParam("itemName"),
      currentQuotedPrice:
        getParam("current_price") || getParam("currentQuotedPrice"),
    };
  };
  const data = useSelector((state) => state.loginData.value);
  const access_type = data?.access_type;
  const user_token = data?.user?.user_token;
  const prUserToken =
    data?.user?.token || data?.user_token || data?.user?.user_token || "";
  const orgToken =
    data?.organization_token || data?.org_token || data?.user?.organization_token || prUserToken;
  const orgId = data?.user?.org_id || data?.org_id || "";
  const authToken = data?.auth_token || data?.access_token || data?.token || "";
  const userDetails = data?.user || {};
  const allowedMenu = useSelector(
    (state) => state.loginData.value?.allowed_menu || [],
  );
  const normalizedAccess = (() => {
    const role = String(access_type || "")
      .trim()
      .toLowerCase();

    if (role.includes("vendor")) return "vendor";
    if (role === "admin") return "buyer_admin";
    if (role.includes("buyer")) return "buyer";
    if (role.includes("user")) return "user";

    return role;
  })();
  const chatMessagesStorageKey = `chat_messages_${user_token || normalizedAccess || "guest"}`;
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(getDefaultMessages);
  const [input, setInput] = useState("");
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [hasLoadedStoredMessages, setHasLoadedStoredMessages] = useState(false);
  const [negotiationBotContext, setNegotiationBotContext] = useState(null);
  const [isLauncherCollapsed, setIsLauncherCollapsed] = useState(false);

  const chatRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const departmentListRef = useRef([]);
  const dragDepthRef = useRef(0);

  const openNegotiationPanel = (context = {}) => {
    setNegotiationBotContext(context);
    setIsOpen(true);
  };

  const closeNegotiationPanel = () => {
    setNegotiationBotContext(null);
  };

  const toggleWidget = () => {
    if (!isOpen && normalizedAccess === "vendor" && !negotiationBotContext) {
      openNegotiationPanel({ mode: "vendor" });
      return;
    }

    setIsOpen((prev) => !prev);
  };

  const toggleLauncher = () => {
    setIsLauncherCollapsed((prev) => {
      if (!prev) {
        setIsOpen(false);
      }

      return !prev;
    });
  };

  useEffect(() => {
    const handleOpenNegotiationBot = (event) => {
      openNegotiationPanel(event.detail || {});
    };

    window.addEventListener(
      NEGOTIATION_BOT_OPEN_EVENT,
      handleOpenNegotiationBot,
    );

    return () => {
      window.removeEventListener(
        NEGOTIATION_BOT_OPEN_EVENT,
        handleOpenNegotiationBot,
      );
    };
  }, []);

  useEffect(() => {
    const urlContext = getNegotiationContextFromUrl();
    if (urlContext) {
      openNegotiationPanel(urlContext);
      return;
    }

  }, []);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 220)}px`;
  }, [input]);

  // Simulate bot typing
  const showBotReply = (replyText) => {
    setIsBotTyping(true);
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: normalizeChatMessageText(replyText) },
      ]);
      setIsBotTyping(false);
    }, 1200);
  };

  const botReply = (text) => {
    setMessages((prev) => [
      ...prev,
      { sender: "bot", text: normalizeChatMessageText(text) },
    ]);
  };

  const updateMessageById = (messageId, updater) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === messageId ? updater(msg) : msg)),
    );
  };

  const getActionConfig = (action) => ACTION_PERMISSION_MAP[action] || null;

  const canAccessAction = (action) => {
    const config = getActionConfig(action);
    if (!config) return false;

    if (
      ["buyer", "buyer_admin"].includes(normalizedAccess) &&
      config.menuId != null &&
      !allowedMenu.includes(config.menuId)
    ) {
      return false;
    }

    return Boolean(config[normalizedAccess]);
  };

  const getRoleAwareSuggestion = (message) => {
    if (
      normalizedAccess === "vendor" &&
      (message === "vendor" || message === "supplier")
    ) {
      return {
        label: "Supplier User",
        action: "navigate_to_vendor_user_management",
      };
    }

    return null;
  };

  const shouldHandleEmailPrExtraction = (message, files = []) => {
    if (normalizedAccess !== "user") return false;
    if (files.length > 0) return true;

    const normalizedMessage = String(message || "")
      .trim()
      .toLowerCase();
    if (!normalizedMessage) return false;

    return (
      EMAIL_PR_TRIGGER_PATTERNS.some((pattern) =>
        normalizedMessage.includes(pattern),
      ) ||
      looksLikeEmailContent(message) ||
      looksLikePrTableContent(message)
    );
  };

  const fetchDepartmentOptions = async () => {
    if (departmentListRef.current.length > 0) {
      return departmentListRef.current;
    }

    const response = await axios.post(
      API_ENDPOINT.get_Department,
      { user_token: prUserToken },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      },
    );

    const departments = Array.isArray(response?.data?.data)
      ? response.data.data
      : [];
    departmentListRef.current = departments;
    return departments;
  };

  

  const sendNewRFQToken = async (token) => {
    if (!token) return;

    try {
      await axios({
        method: "POST",
        url: `${API_ENDPOINT.rfq_newversion_chatgpt}`,
        data: {
          rfq_token: token,
          org_token: orgToken,
        },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
    } catch (error) {
      console.error("Unable to notify new RFQ token:", error);
    }
  };

  const buildEmailPrPreviewMessage = (responseData, departments = []) => {
    const previewId = `email-pr-${Date.now()}`;
    const savePayload = responseData?.save_payload || {};
    const extractedData = responseData?.data || {};
    const items = Array.isArray(savePayload?.items)
      ? savePayload.items
      : Array.isArray(extractedData?.items)
        ? extractedData.items
        : [];
    const derivedReason = buildEmailPrReasonFromItems(items);

    return {
      id: previewId,
      sender: "bot",
      type: "email_pr_preview",
      text: " Review the items, choose the department, and create the PR.",
      preview: {
        requestReason:
          derivedReason ||
          savePayload?.purchase_reason ||
          extractedData?.request_reason ||
          "",
        notes: extractedData?.notes || "",
        totalPrice: savePayload?.total_price ?? extractedData?.total_price ?? 0,
        items,
        savePayload,
        departments,
        selectedDepartmentId: "",
        status: "ready",
        error: "",
        successMessage: "",
      },
    };
  };

  const handleExtractPrFromEmail = async (messageText, files = []) => {
    const hasFile = files.length > 0;

    try {
      const requestConfig = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };

      let response;

      if (hasFile) {
        const formData = new FormData();
        formData.append("user_token", prUserToken);
        formData.append("org_token", orgToken);
        formData.append("org_id", orgId);
        formData.append("file", files[0]);

        response = await axios.post(
          API_ENDPOINT.create_pr_from_email_request,
          formData,
          {
            headers: {
              ...requestConfig.headers,
              "Content-Type": "multipart/form-data",
            },
          },
        );
      } else {
        response = await axios.post(
          API_ENDPOINT.create_pr_from_email_request,
          {
            user_token: prUserToken,
            org_token: orgToken,
            org_id: orgId,
            email_text: messageText,
          },
          requestConfig,
        );
      }

      let responseData = response?.data || {};
      if (!responseData?.success) {
        const localResponse = !hasFile
          ? buildLocalEmailPrExtractionResponse(messageText)
          : null;

        if (localResponse) {
          responseData = localResponse;
        } else {
          botReply(
            responseData?.response ||
              "I couldn't extract purchase request details from that email.",
          );
          return;
        }
      }

      const departments = await fetchDepartmentOptions();
      const previewMessage = buildEmailPrPreviewMessage(
        responseData,
        departments,
      );

      setMessages((prev) => [...prev, previewMessage]);
    } catch (error) {
      console.error("Email PR extraction failed:", error);
      const localResponse = !hasFile
        ? buildLocalEmailPrExtractionResponse(messageText)
        : null;

      if (localResponse) {
        try {
          const departments = await fetchDepartmentOptions();
          const previewMessage = buildEmailPrPreviewMessage(
            localResponse,
            departments,
          );
          setMessages((prev) => [...prev, previewMessage]);
          return;
        } catch (fallbackError) {
          console.error("Local email PR fallback failed:", fallbackError);
        }
      }

      botReply(
        error?.response?.data?.response ||
          "I couldn't extract the purchase request from that email right now.",
      );
    } finally {
      setIsBotTyping(false);
    }
  };

  const submitExtractedPr = async (messageId) => {
    const previewMessage = messages.find((msg) => msg.id === messageId);
    const preview = previewMessage?.preview;

    if (!preview) return;

    if (!preview.selectedDepartmentId) {
      updateMessageById(messageId, (msg) => ({
        ...msg,
        preview: {
          ...msg.preview,
          error: "Please choose a department before creating the PR.",
        },
      }));
      return;
    }

    const selectedDepartment = (preview.departments || []).find(
      (dept) => String(dept.id) === String(preview.selectedDepartmentId),
    );

    updateMessageById(messageId, (msg) => ({
      ...msg,
      preview: {
        ...msg.preview,
        status: "submitting",
        error: "",
        successMessage: "",
      },
    }));

    try {
      const now = new Date();
      const oneMonthLater = new Date(now);
      oneMonthLater.setMonth(now.getMonth() + 1);
      const bidEndTime = new Date(oneMonthLater);
      bidEndTime.setHours(23, 59, 59, 999);

      const savePayload = preview.savePayload || {};
      const extractedItemsSource =
        Array.isArray(savePayload.items) && savePayload.items.length > 0
          ? savePayload.items
          : preview.items || [];
      const normalizedItems = mapExtractedItemsForPrSave(extractedItemsSource);
      const derivedReason = buildEmailPrReasonFromItems(normalizedItems);

      if (normalizedItems.length === 0) {
        updateMessageById(messageId, (msg) => ({
          ...msg,
          preview: {
            ...msg.preview,
            status: "ready",
            error: "No valid extracted items were found to create the PR.",
            successMessage: "",
          },
        }));
        return;
      }

      const formData = new FormData();
      const extractedPrCode = String(savePayload["PR Code"] || "").trim();
      const prCode = /^PR-\d{10,}(?:\/|$)/i.test(extractedPrCode)
        ? ""
        : extractedPrCode;
      formData.append("user_token", prUserToken);
      formData.append("org_id", orgId);
      formData.append("PR Code", prCode);
      formData.append("Bid Type", 0);
      formData.append("Proposal Document", "");
      formData.append("purchase_request_number", "");
      formData.append(
        "shipping_address1",
        savePayload.shipping_address || userDetails.address_line || "",
      );
      formData.append("shipping_address2", "");
      formData.append("city", userDetails.city || "");
      formData.append("state", userDetails.state || "");
      formData.append("country", userDetails.country || "");
      formData.append("mobile_number", userDetails.phone_number || "");
      formData.append("pincode", userDetails.postal_code || "");
      formData.append(
        "purchase_reason",
        derivedReason ||
          savePayload.purchase_reason ||
          preview.requestReason ||
          "",
      );
      formData.append("dept_name", selectedDepartment?.name || "");
      formData.append("bid_from_date", now.toISOString());
      formData.append("bid_to_date", oneMonthLater.toISOString());
      formData.append("bid_time", bidEndTime.toISOString());
      formData.append("items", JSON.stringify(normalizedItems));
      formData.append(
        "budgetDetails",
        JSON.stringify(savePayload.budgetDetails || []),
      );
      formData.append("approvers", JSON.stringify([]));
      formData.append("vendorInfo", JSON.stringify([]));
      
      const prResponse = await axios.post(API_ENDPOINT.ins_purchase_request, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const savedPrCode = prResponse?.data?.pr_code || prCode;
      const quickRfqFormData = cloneFormData(formData);
      quickRfqFormData.set("purchase_request_number", savedPrCode);
      quickRfqFormData.set("RFQ Code", savedPrCode);

      const quickRfqResponse = await axios.post(
        API_ENDPOINT.save_quick_rfq,
        quickRfqFormData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      await sendNewRFQToken(quickRfqResponse?.data?.rfq_token);

      updateMessageById(messageId, (msg) => ({
        ...msg,
        preview: {
          ...msg.preview,
          status: "submitted",
          error: "",
          successMessage:
            "PR created successfully and moved to RFQ. Opening the PR table...",
        },
      }));

      window.setTimeout(() => {
        const userPrRoute =
          ACTION_PERMISSION_MAP.navigate_to_user_pr?.user ||
          "#/User_dashboard/PR";
        sessionStorage.setItem(
          "CHAT_ACTION",
          JSON.stringify({
            action: "refresh_pr_list",
            source: "email_pr_chatbot",
            ts: Date.now(),
          }),
        );
        window.dispatchEvent(
          new CustomEvent("chat-pr-created", {
            detail: {
              source: "email_pr_chatbot",
              route: userPrRoute,
              ts: Date.now(),
            },
          }),
        );
        window.location.hash = "";
        window.setTimeout(() => {
          window.location.hash = userPrRoute;
        }, 0);
        setIsOpen(false);
      }, 900);
    } catch (error) {
      console.error("Email PR creation failed:", error);
      const quickRfqFailed = error?.config?.url === API_ENDPOINT.save_quick_rfq;
      updateMessageById(messageId, (msg) => ({
        ...msg,
        preview: {
          ...msg.preview,
          status: quickRfqFailed ? "submitted" : "ready",
          error: quickRfqFailed
            ? ""
            : error?.response?.data?.response ||
              "Unable to create the PR right now.",
          successMessage: quickRfqFailed
            ? "PR created successfully, but RFQ conversion did not complete."
            : "",
        },
      }));
    }
  };

  const buildAttachmentMeta = (files = []) =>
    files.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
    }));

  const addAttachmentFiles = (files = []) => {
    const incomingFiles = Array.from(files || []).filter(Boolean);
    if (!incomingFiles.length) return;

    setAttachedFiles((prev) => {
      const seen = new Set(
        prev.map((file) => `${file.name}-${file.size}-${file.lastModified}`),
      );
      const nextFiles = [...prev];

      incomingFiles.forEach((file) => {
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        if (!seen.has(key)) {
          seen.add(key);
          nextFiles.push(file);
        }
      });

      return nextFiles;
    });
  };

  const handleAttachmentChange = (event) => {
    addAttachmentFiles(event.target.files);

    event.target.value = null;
  };

  const hasDraggedFiles = (event) =>
    Array.from(event.dataTransfer?.types || []).includes("Files");

  const handleFileDragEnter = (event) => {
    if (!hasDraggedFiles(event)) return;

    event.preventDefault();
    if (isBotTyping) return;

    dragDepthRef.current += 1;
    setIsDraggingFile(true);
  };

  const handleFileDragOver = (event) => {
    if (!hasDraggedFiles(event)) return;

    event.preventDefault();
    if (isBotTyping) {
      event.dataTransfer.dropEffect = "none";
      return;
    }

    event.dataTransfer.dropEffect = "copy";
    setIsDraggingFile(true);
  };

  const handleFileDragLeave = (event) => {
    if (!hasDraggedFiles(event)) return;

    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

    if (dragDepthRef.current === 0) {
      setIsDraggingFile(false);
    }
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDraggingFile(false);

    if (isBotTyping) return;

    addAttachmentFiles(event.dataTransfer?.files);
  };

  const handleRemoveAttachment = (fileName) => {
    setAttachedFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const handlePaste = (event) => {
    const normalizedText = normalizeClipboardPayload(event.clipboardData);
    if (!normalizedText) return;

    event.preventDefault();
    const target = event.target;
    const start = target.selectionStart ?? input.length;
    const end = target.selectionEnd ?? input.length;

    setInput((prev) => {
      const nextValue = `${prev.slice(0, start)}${normalizedText}${prev.slice(end)}`;
      return nextValue;
    });
  };

  const handleSend = async () => {
    if ((!input.trim() && attachedFiles.length === 0) || isBotTyping) return;

    const typedMessage = input.trim();

    const userMessage =
      typedMessage.length > 0
        ? normalizeChatMessageText(typedMessage)
        : attachedFiles.length > 0
          ? "Uploaded email document for PR extraction."
          : "";
    const filesForMessage = [...attachedFiles];

    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        text: userMessage,
        attachments: buildAttachmentMeta(filesForMessage),
      },
    ]);
    setInput("");
    setAttachedFiles([]);
    setIsBotTyping(true);

    if (shouldHandleEmailPrExtraction(userMessage, filesForMessage)) {
      await handleExtractPrFromEmail(userMessage, filesForMessage);
      return;
    }

    try {
      const res = await fetch(`${API_ENDPOINT.chat_bot_api}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: userMessage,
          user_token: user_token,
          access_type: access_type,
        }),
      });

      const data = await res.json();
      const chatbotData = data?.data || {};
      const chatbotAction = data?.action || null;
      setIsBotTyping(false);

      if (data.type === "menu") {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            type: "menu",
            menus: data.menus,
            text: data.text || "Here are your available options:",
          },
        ]);
        return;
      }
      const prCodeFromMessage = chatbotData.pr_code || extractPRCode(userMessage);
      const rfqCodeFromMessage = chatbotData.rfq_code || extractRFQCode(userMessage);
      const poSearchValue = chatbotData.po_code || extractPOSearchValue(userMessage);
      const frontendSuggest = findFrontendSuggestion(userMessage);
      const userSearchText = chatbotData.search_text || extractUserSearchText(userMessage);
      const vendorUserSearchText = chatbotData.user_name || extractVendorUSerSearch(userMessage);
      const invoiceProcessSearch = chatbotData.invoice_code || extractInvoiceOrPONumber(userMessage);
      const vendorMasterSearchText = chatbotData.vendor_code || extractVendorMasterSearch(userMessage);
      const masterSearch = extractMasterSearch(userMessage);
      const ApproverRangeSearch = extractApproverRangeMasterSearch(userMessage);
      const threeWaySearch = chatbotData.three_way_search || extractThreeWaySearchText(userMessage);
      const comparisonSearch = extractComparisonSearch(userMessage);
      const GRNSearch = chatbotData.grn_code || extractGrnSearchText(userMessage);
      const rcOpen = chatbotData.rc_number || extractRCNumber(userMessage);
      const rcToPo = chatbotData.item_values?.length
        ? { item_values: chatbotData.item_values }
        : extractRcToPoData(userMessage);

      if (frontendSuggest && !chatbotAction) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            type: "suggestion",
            text: "I found something related!",
            label: frontendSuggest.label,
            action: frontendSuggest.action,
          },
        ]);
        setIsBotTyping(false);
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: normalizeChatMessageText(
            data.response ||
              "I apologize, I didn't quite understand that. Could you please rephrase?",
          ),
        },
      ]);

      if (chatbotAction) {
        if (chatbotAction === "create_twm" && filesForMessage.length > 0) {
          setPendingChatbotUpload({
            action: chatbotAction,
            files: filesForMessage,
            message: userMessage,
            createdAt: Date.now(),
          });
        } else {
          clearPendingChatbotUpload();
        }

        redirection_function({
          action: chatbotAction,
          status: chatbotData.status ?? data.status,
          pr_code: prCodeFromMessage,
          rfq_code: rfqCodeFromMessage,
          po_code: poSearchValue,
          search_text: userSearchText,
          invoice_process_code: invoiceProcessSearch,
          vendor_user_search: vendorUserSearchText,
          vendor_Master_search: vendorMasterSearchText,
          buyerAdmin_master_search: masterSearch,
          buyerAdmin_approverRange_search: ApproverRangeSearch,
          three_way_search: threeWaySearch,
          comparison_search: comparisonSearch,
          grn_search: GRNSearch,
          rc_number_extract: rcOpen,
          rc_to_po: rcToPo,
          item_code: chatbotData.item_code,
          vendor_code: chatbotData.vendor_code,
          user_name: chatbotData.user_name,
          comparison_code: chatbotData.comparison_code,
          price_catalog_token: chatbotData.price_catalog_token,
          free_rfq_token: chatbotData.free_rfq_token,
          short_code: chatbotData.short_code,
        });
      }
    } catch (error) {
      console.error("Chatbot error:", error);
      setIsBotTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: " Sorry, I'm having trouble connecting right now. Please try again in a moment.",
        },
      ]);
    }
  };

  const findFrontendSuggestion = (message) => {
    const msg = message.toLowerCase().trim();
    const roleAwareSuggestion = getRoleAwareSuggestion(msg);

    if (roleAwareSuggestion && canAccessAction(roleAwareSuggestion.action)) {
      return roleAwareSuggestion;
    }

    const wordCount = msg.split(/\s+/).length;
    if (wordCount > 2) return null;

    for (const key in KEYWORD_SUGGESTIONS) {
      const regex = new RegExp(`^${key}$`, "i");

      if (regex.test(msg)) {
        const suggestion = KEYWORD_SUGGESTIONS[key];
        if (canAccessAction(suggestion.action)) {
          return suggestion;
        }
      }
    }

    return null;
  };

  const redirection_function = ({
    action,
    pr_code,
    rfq_code,
    po_code,
    search_text,
    invoice_process_code,
    vendor_user_search,
    vendor_Master_search,
    buyerAdmin_master_search,
    buyerAdmin_approverRange_search,
    status,
    three_way_search,
    comparison_search,
    grn_search,
    rc_number_extract,
    rc_to_po,
    item_code,
    vendor_code,
    user_name,
    comparison_code,
    price_catalog_token,
    free_rfq_token,
    short_code,
  }) => {
    const config = getActionConfig(action);

    if (!config) {
      botReply("❌ You do not have permission for this module.");
      return;
    }

    // Menu-based validation ONLY for Buyer & Buyer Admin
    if (
      ["buyer", "buyer_admin"].includes(normalizedAccess) &&
      config.menuId != null &&
      !allowedMenu.includes(config.menuId)
    ) {
      botReply("❌ You do not have access for this module.");
      return;
    }

    const route = config[normalizedAccess];

    if (!route) {
      botReply("❌ Access denied for your role.");
      return;
    }

    sessionStorage.setItem(
      "CHAT_ACTION",
      JSON.stringify({
        action,
        pr_code,
        rfq_code,
        po_code,
        search_text,
        invoice_process_code,
        vendor_user_search,
        vendor_Master_search,
        buyerAdmin_master_search,
        buyerAdmin_approverRange_search,
        status,
        three_way_search,
        comparison_search,
        grn_search,
        rc_number_extract,
        item_values: rc_to_po?.item_values,
        item_code,
        vendor_code,
        user_name,
        comparison_code,
        price_catalog_token,
        free_rfq_token,
        short_code,
      }),
    );

    window.location.hash = "";
    setTimeout(() => {
      window.location.hash = route;
    }, 0);
  };

  // Restore from localStorage
  useEffect(() => {
    setHasLoadedStoredMessages(false);

    try {
      const saved = localStorage.getItem(chatMessagesStorageKey);
      setMessages(saved ? JSON.parse(saved) : getDefaultMessages());
    } catch (error) {
      console.error("Unable to restore chatbot history:", error);
      setMessages(getDefaultMessages());
    } finally {
      setHasLoadedStoredMessages(true);
    }
  }, [chatMessagesStorageKey]);

  useEffect(() => {
    if (!hasLoadedStoredMessages) return;

    localStorage.setItem(chatMessagesStorageKey, JSON.stringify(messages));
  }, [chatMessagesStorageKey, hasLoadedStoredMessages, messages]);

  const clearChat = () => {
    // Clear chat history
    localStorage.removeItem(chatMessagesStorageKey);
    localStorage.removeItem("chat_messages");
    sessionStorage.removeItem("CHAT_ACTION");
    clearPendingChatbotUpload();
    window.dispatchEvent(new Event("chat-cleared"));
    setMessages(getDefaultMessages());
    setAttachedFiles([]);
    closeNegotiationPanel();

    clearChatURLParams();
  };

  const shouldUseWideChatWidget =
    attachedFiles.length > 0 ||
    isDraggingFile ||
    messages.some((message) => message?.type === "email_pr_preview");

  return (
    <>
      <div
        className={`${styles.chatLauncherDock} ${
          isLauncherCollapsed ? styles.chatLauncherCollapsed : ""
        }`}
      >
        <button
          type="button"
          className={styles.chatLauncherToggle}
          onClick={toggleLauncher}
          aria-label={
            isLauncherCollapsed ? "Show chat assistant" : "Hide chat assistant"
          }
          title={
            isLauncherCollapsed ? "Show chat assistant" : "Hide chat assistant"
          }
        >
          <span className={styles.chatLauncherChevron} aria-hidden="true" />
        </button>

        <button
          type="button"
          className={styles.chatFloatingBtn}
          onClick={toggleWidget}
          aria-label={isOpen ? "Close chat assistant" : "Open chat assistant"}
          title={isOpen ? "Close chat assistant" : "Open chat assistant"}
        >
          <ChatBubble sx={{ fontSize: 21 }} />
        </button>
      </div>

      {isOpen && (
        <div
          ref={chatRef}
          className={`${styles.chatWidgetContainer} ${
            isDraggingFile ? styles.chatWidgetDragActive : ""
          } ${
            shouldUseWideChatWidget
              ? styles.chatWidgetWithUpload
              : ""
          } ${negotiationBotContext ? styles.chatWidgetNegotiation : ""}`}
          onDragEnter={handleFileDragEnter}
          onDragOver={handleFileDragOver}
          onDragLeave={handleFileDragLeave}
          onDrop={handleFileDrop}
        >
          {isDraggingFile && (
            <div className={styles.chatDropOverlay}>
              <AttachFileRounded sx={{ fontSize: 24 }} />
              <span>Drop files to attach</span>
            </div>
          )}
          {/* Header */}
          <div className={styles.chatHeader}>
            <div className={styles.chatHeaderIdentity}>
              <div className={styles.chatAvatar}>
                <img
                  src={bidzHeaderLogo}
                  alt="bidZ.ai"
                  className={styles.chatAvatarLogo}
                />
              </div>
              <div className={styles.chatHeaderCopy}>
                <div className={styles.chatHeaderTitle}>bidZ.ai Assistant</div>
                <div className={styles.chatStatus}>
                  <span className={styles.chatStatusDot}></span>
                  {isBotTyping ? "Typing now" : "Online now"}
                </div>
              </div>
            </div>

            <div className={styles.chatHeaderActions}>
              <button className={styles.chatClearBtn} onClick={clearChat}>
                <DeleteOutline sx={{ fontSize: 16 }} /> Clear
              </button>
              <Close
                className={styles.chatCloseBtn}
                onClick={() => setIsOpen(false)}
              />
            </div>
          </div>

          {/* Messages */}
          <div className={styles.chatMessages}>
            {negotiationBotContext ? (
              <NegotiationBot
                context={negotiationBotContext}
                onClose={closeNegotiationPanel}
              />
            ) : (
              messages.map((msg, idx) => (
              <div
                key={idx}
                className={styles.messageContainer}
                style={{
                  "--justify-content":
                    msg.sender === "bot" ? "flex-start" : "flex-end",
                }}
              >
                {msg.sender === "bot" && (
                  <div
                    className={`${styles.messageAvatar} ${styles.botAvatar}`}
                  >
                    <img
                      src={bidzBotLogo}
                      alt="bidZ.ai"
                      className={styles.messageAvatarLogo}
                    />
                  </div>
                )}

                <div
                  className={`${styles.messageBubble} ${
                    isChatTableText(msg.text) ? styles.tableMessageBubble : ""
                  }`}
                >
                  {msg.type === "menu" ? (
                    <div className={styles.menuContainer}>
                      <div className={styles.menuTitle}>{msg.text}</div>
                      <div className={styles.menuOptions}>
                        {msg.menus.map((menu, i) => (
                          <button
                            key={i}
                            className={styles.menuOptionBtn}
                            onClick={() =>
                              (window.location.hash = menu.menu_route)
                            }
                          >
                            {menu.menu_label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : msg.type === "suggestion" ? (
                    <div className={styles.menuContainer}>
                      <div
                        className={styles.menuTitle}
                        style={{ fontWeight: 600 }}
                      >
                        {msg.text}
                      </div>

                      <button
                        className={styles.menuOptionBtn}
                        onClick={() =>
                          redirection_function({ action: msg.action })
                        }
                      >
                        {msg.label}
                      </button>
                    </div>
                  ) : msg.type === "email_pr_preview" ? (
                    <div className={styles.emailPrCard}>
                      <div className={styles.emailPrHeader}>
                        <div className={styles.emailPrHeaderIcon}>
                          <ReceiptLongOutlined sx={{ fontSize: 18 }} />
                        </div>
                        <div className={styles.emailPrHeaderCopy}>
                          <div className={styles.emailPrEyebrow}>
                            Purchase Request Draft
                          </div>
                          <div className={styles.emailPrTitle}>{msg.text}</div>
                        </div>
                      </div>

                      <div className={styles.emailPrSummary}>
                        <div className={styles.emailPrSummaryItem}>
                          <span>Items</span>
                          <strong>{(msg.preview?.items || []).length}</strong>
                        </div>
                        <div className={styles.emailPrSummaryItem}>
                          <span>Total</span>
                          <strong>
                            {Number(
                              msg.preview?.totalPrice || 0,
                            ).toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </strong>
                        </div>
                        <div className={styles.emailPrSummaryItem}>
                          <span>Department</span>
                          <strong>
                            {(msg.preview?.departments || []).find(
                              (department) =>
                                String(department.id) ===
                                String(msg.preview?.selectedDepartmentId || ""),
                            )?.name || "Pending"}
                          </strong>
                        </div>
                      </div>

                      {/* {msg.preview?.requestReason ? (
                        <div className={styles.emailPrNotes}>
                          <span className={styles.emailPrNotesLabel}>
                            Reason
                          </span>
                          <p>{msg.preview.requestReason}</p>
                        </div>
                      ) : null} */}

                      {/* {msg.preview?.notes ? (
                        <div className={styles.emailPrNotes}>
                          <span className={styles.emailPrNotesLabel}>
                            Notes
                          </span>
                          <p>{msg.preview.notes}</p>
                        </div>
                      ) : null} */}

                      <div className={styles.emailPrSectionLabel}>
                        Extracted Items
                      </div>
                      <div className={styles.emailPrList}>
                        {(msg.preview?.items || []).length > 0 ? (
                          (msg.preview?.items || []).map((item, itemIndex) => (
                            <div
                              key={`${item.item_code || item.item_name || "item"}-${itemIndex}`}
                              className={styles.emailPrItem}
                            >
                              <div className={styles.emailPrItemHeader}>
                                <div className={styles.emailPrItemName}>
                                  <Inventory2Outlined sx={{ fontSize: 16 }} />
                                  <span>
                                    {item.item_name || "Unnamed item"}
                                  </span>
                                </div>
                                <span className={styles.emailPrItemQty}>
                                  {item.quantity || "0"}{" "}
                                  {item.unit_measure || ""}
                                </span>
                              </div>
                              <div className={styles.emailPrItemMetaGrid}>
                                <div className={styles.emailPrItemMeta}>
                                  <span>Code</span>
                                  <strong>{item.item_code || "-"}</strong>
                                </div>
                                <div className={styles.emailPrItemMeta}>
                                  <span>Category</span>
                                  <strong>{item.category || "-"}</strong>
                                </div>
                                <div className={styles.emailPrItemMeta}>
                                  <span>HSN</span>
                                  <strong>{item.hsn_code || "-"}</strong>
                                </div>
                                <div className={styles.emailPrItemMeta}>
                                  <span>UNSPSC</span>
                                  <strong>{item.unspsc_code || "-"}</strong>
                                </div>
                                <div className={styles.emailPrItemMeta}>
                                  <span>Unit Price</span>
                                  <strong>
                                    {formatCurrency(item.unit_price)}
                                  </strong>
                                </div>
                              </div>
                              <div className={styles.emailPrItemDescription}>
                                {item.description || "No description provided."}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className={styles.emailPrEmpty}>
                            No items were extracted from the email.
                          </div>
                        )}
                      </div>

                      <div className={styles.emailPrActionPanel}>
                        <label className={styles.emailPrLabel}>
                          Choose department
                        </label>
                        <div className={styles.emailPrSelectWrap}>
                          <ApartmentRounded sx={{ fontSize: 18 }} />
                          <select
                            className={styles.emailPrSelect}
                            value={msg.preview?.selectedDepartmentId || ""}
                            disabled={
                              msg.preview?.status === "submitting" ||
                              msg.preview?.status === "submitted"
                            }
                            onChange={(event) => {
                              const selectedDepartmentId = event.target.value;
                              updateMessageById(msg.id, (currentMessage) => ({
                                ...currentMessage,
                                preview: {
                                  ...currentMessage.preview,
                                  selectedDepartmentId,
                                  error: "",
                                },
                              }));
                            }}
                          >
                            <option value="">Select department</option>
                            {(msg.preview?.departments || []).map(
                              (department) => (
                                <option
                                  key={department.id}
                                  value={department.id}
                                >
                                  {department.name}
                                </option>
                              ),
                            )}
                          </select>
                        </div>

                        {msg.preview?.error ? (
                          <div className={styles.emailPrError}>
                            {msg.preview.error}
                          </div>
                        ) : null}
                        {msg.preview?.successMessage ? (
                          <div className={styles.emailPrSuccess}>
                            {msg.preview.successMessage}
                          </div>
                        ) : null}

                        <button
                          type="button"
                          className={styles.emailPrAction}
                          disabled={
                            msg.preview?.status === "submitting" ||
                            msg.preview?.status === "submitted"
                          }
                          onClick={() => submitExtractedPr(msg.id)}
                        >
                          <SendRounded sx={{ fontSize: 16 }} />
                          {msg.preview?.status === "submitting"
                            ? "Creating PR..."
                            : msg.preview?.status === "submitted"
                              ? "PR Created"
                              : "Create PR"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`${styles.messageContent} ${
                        msg.sender === "bot"
                          ? styles.botMessage
                          : styles.userMessage
                      } ${
                        isChatTableText(msg.text) ? styles.tableMessageContent : ""
                      }`}
                    >
                      <MessageText text={msg.text} />
                      {Array.isArray(msg.attachments) &&
                        msg.attachments.length > 0 && (
                          <div className={styles.messageAttachments}>
                            {msg.attachments.map((file, fileIdx) => (
                              <div
                                key={`${file.name}-${file.size}-${fileIdx}`}
                                className={styles.messageAttachment}
                              >
                                <DescriptionOutlined sx={{ fontSize: 14 }} />
                                <span>{file.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  )}
                </div>

                {msg.sender === "user" && (
                  <div
                    className={`${styles.messageAvatar} ${styles.userAvatar}`}
                  >
                    U
                  </div>
                )}
              </div>
              ))
            )}

            {!negotiationBotContext && isBotTyping && (
              <div
                className={styles.messageContainer}
                style={{ "--justify-content": "flex-start" }}
              >
                <div className={`${styles.messageAvatar} ${styles.botAvatar}`}>
                  <img
                    src={bidzBotLogo}
                    alt="bidZ.ai"
                    className={styles.messageAvatarLogo}
                  />
                </div>
                <div className={styles.typingDots}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {!negotiationBotContext && (
          <div className={styles.chatInputContainer}>
            {attachedFiles.length > 0 && (
              <div className={styles.chatAttachmentTray}>
                {attachedFiles.map((file) => (
                  <div
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className={styles.chatAttachmentChip}
                  >
                    <DescriptionOutlined sx={{ fontSize: 14 }} />
                    <span className={styles.chatAttachmentName}>
                      {file.name}
                    </span>
                    <button
                      type="button"
                      className={styles.chatAttachmentRemove}
                      onClick={() => handleRemoveAttachment(file.name)}
                      disabled={isBotTyping}
                    >
                      <Close sx={{ fontSize: 14 }} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.chatInputWrapper}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleAttachmentChange}
              />

              <button
                type="button"
                className={styles.chatAttachBtn}
                onClick={() => fileInputRef.current?.click()}
                disabled={isBotTyping}
                title="Attach files"
              >
                <AttachFileRounded sx={{ fontSize: 18 }} />
                {/* <span>Attach</span> */}
              </button>

              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onPaste={handlePaste}
                placeholder="Type a message or paste an email request..."
                className={styles.chatInput}
                disabled={isBotTyping}
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !isBotTyping) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />

              <button
                className={styles.chatSendBtn}
                onClick={handleSend}
                disabled={
                  (!input.trim() && attachedFiles.length === 0) || isBotTyping
                }
              >
                <SendRounded sx={{ fontSize: 16 }} /> <span>Send</span>
              </button>
            </div>
          </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
